import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import Coin from '../components/Coin';

const CombatDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [combat, setCombat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [betAmount, setBetAmount] = useState('');
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [placingBet, setPlacingBet] = useState(false);

  useEffect(() => {
    fetchCombatDetail();
  }, [id]);

  const fetchCombatDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/combats/${id}`);
      setCombat(response.data.combat || response.data);
    } catch (err) {
      setError('Erreur lors du chargement du combat');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceBet = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!betAmount || !selectedWinner) {
      alert('Veuillez sélectionner un gagnant et entrer un montant');
      return;
    }

    try {
      setPlacingBet(true);
      await api.post('/bets', {
        combatId: parseInt(id),
        selectedCock: selectedWinner,
        amount: parseFloat(betAmount)
      });
      
      alert('Pari placé avec succès !');
      // Rafraîchir les données et rediriger vers la page des combats
      await fetchCombatDetail();
      setBetAmount('');
      setSelectedWinner(null);
      // Optionnel : rediriger vers la liste des combats après un délai
      setTimeout(() => {
        navigate('/combats');
      }, 2000);
    } catch (err) {
      alert('Erreur lors du placement du pari: ' + (err.response?.data?.message || err.message));
    } finally {
      setPlacingBet(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'upcoming': { text: 'À venir', color: 'bg-blue-100 text-blue-800' },
      'active': { text: 'En cours', color: 'bg-green-100 text-green-800' },
      'completed': { text: 'Terminé', color: 'bg-gray-100 text-gray-800' },
      'cancelled': { text: 'Annulé', color: 'bg-red-100 text-red-800' }
    };
    
    const config = statusConfig[status] || statusConfig['upcoming'];
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du combat...</p>
        </div>
      </div>
    );
  }

  if (error || !combat) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600">{error || 'Combat non trouvé'}</p>
          <button 
            onClick={() => navigate('/combats')}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Retour aux combats
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header avec bouton retour */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/combats')}
            className="flex items-center text-orange-500 hover:text-orange-600 mb-4"
          >
            <span className="mr-2">←</span>
            Retour aux combats
          </button>
        </div>

        {/* Image principale */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="h-64 bg-gradient-to-br from-orange-400 to-red-500 relative">
            {combat.image && (
              <img 
                src={combat.image} 
                alt={`Combat ${combat.id}`}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute top-4 right-4">
              {getStatusBadge(combat.status)}
            </div>
          </div>
        </div>

        {/* Informations du combat */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {combat.title || `Combat #${combat.id}`}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-3">
              <div className="flex items-center">
                <span className="text-gray-500 w-24">📅 Date:</span>
                <span className="font-medium">{formatDate(combat.scheduled_time)}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 w-24">🐓 Coq 1:</span>
                <span className="font-medium">{combat.cock1_name} (Cote: {combat.cock1_odds})</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 w-24">🐓 Coq 2:</span>
                <span className="font-medium">{combat.cock2_name} (Cote: {combat.cock2_odds})</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center">
                <span className="text-gray-500 w-24">💰 Total paris:</span>
                <span className="font-medium"><Coin amount={parseFloat(combat.total_bets || 0).toFixed(2)} symbol="HTG" /></span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 w-24">🎲 Nombre paris:</span>
                <span className="font-medium">{combat.total_bets_count || 0}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 w-24">🏆 Gagnant:</span>
                <span className="font-medium">{combat.winner || 'Non déterminé'}</span>
              </div>
            </div>
          </div>

          {combat.description && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-600">{combat.description}</p>
            </div>
          )}
        </div>

        {/* Section des paris */}
        {user && combat.status === 'upcoming' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Placer un pari</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner le gagnant
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { id: 'cock1', name: combat.cock1_name || 'Coq 1', odds: combat.cock1_odds },
                    { id: 'cock2', name: combat.cock2_name || 'Coq 2', odds: combat.cock2_odds }
                  ].map((coq) => (
                    <button
                      key={coq.id}
                      onClick={() => setSelectedWinner(coq.id)}
                      className={`p-4 border-2 rounded-lg text-center transition-colors ${
                        selectedWinner === coq.id
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-2xl mb-2">🐓</div>
                      <div className="font-medium">{coq.name}</div>
                      <div className="text-sm text-gray-600">Cote: {coq.odds}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant du pari
                </label>
                <input
                  type="number"
                  value={betAmount}
                  onChange={(e) => setBetAmount(e.target.value)}
                  placeholder="Entrez le montant"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  min="1"
                />
              </div>

              <button
                onClick={handlePlaceBet}
                disabled={placingBet || !betAmount || !selectedWinner}
                className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {placingBet ? 'Placement du pari...' : 'Placer le pari'}
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/combats')}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600"
                >
                  Voir tous les combats
                </button>
                <button
                  onClick={() => navigate('/my-bets')}
                  className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600"
                >
                  Mes paris
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mes paris sur ce combat */}
        {user && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Mes paris sur ce combat</h2>
            {combat.userBets && combat.userBets.length > 0 ? (
              <div className="space-y-3">
                {combat.userBets.map((bet, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <div>
                      <span className="font-medium">Votre pari sur {bet.selected_cock === 'cock1' ? combat.cock1_name : combat.cock2_name}</span>
                      <span className="text-gray-600 ml-2">(Cote: {bet.odds_at_time})</span>
                    </div>
                    <div className="font-medium text-orange-600">
                      <Coin amount={parseFloat(bet.amount).toFixed(2)} symbol="HTG" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">Vous n'avez pas encore placé de pari sur ce combat.</p>
            )}
          </div>
        )}

        {/* Statistiques des paris */}
        {combat.betStats && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Statistiques des paris</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">{combat.cock1_name}</h3>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Nombre de paris:</span>
                    <span className="font-medium">{combat.betStats.cock1?.bet_count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Montant total:</span>
                    <span className="font-medium"><Coin amount={parseFloat(combat.betStats.cock1?.total_amount || 0).toFixed(2)} symbol="HTG" /></span>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="font-medium text-green-900 mb-2">{combat.cock2_name}</h3>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Nombre de paris:</span>
                    <span className="font-medium">{combat.betStats.cock2?.bet_count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Montant total:</span>
                    <span className="font-medium"><Coin amount={parseFloat(combat.betStats.cock2?.total_amount || 0).toFixed(2)} symbol="HTG" /></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CombatDetail; 